import { Inject, Injectable, Optional, inject } from "@angular/core";
import { take } from 'rxjs/operators';
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { Observable, firstValueFrom, map } from "rxjs";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Meta, Title } from "@angular/platform-browser";
import { REQUEST } from "@nguniversal/express-engine/tokens";
import { Request } from "express";
import { SeoService } from "src/app/services/seo.service";
import { DataService } from "src/app/services/data.service";
import { buildSEOImageUrl } from "src/app/utils/seo-image.helper";

@Injectable({ providedIn: "root" })
export class AIImageResolver implements Resolve<any> {
  afs = inject(AngularFirestore);
  illusData: any = [];
  constructor(
    private meta: Meta, // private socialService: SocialService,
    private title: Title,
    public router: Router,
    private seo: SeoService,
    private dataService: DataService,
  ) { }
  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<any> {
    const slug = route.paramMap.get("slug");
    const ref = this.afs.collection("filesMaster", (ref) =>
      ref.where("slug", "==", slug).orderBy("createdOn", "desc")
    );
    const data$ = ref.valueChanges().pipe(
      map((res) => {
        this.populateMetaTags(res[0], route);
        return res[0];
      })
    ); // Get single item

    return await firstValueFrom(data$);
  }


  populateMetaTags(data, route) {
    //console.log(route.paramMap);
    const title = "AI Images | Illusbook";
    const desc =
      "Discover AI images on Illusbook. Download high-quality visuals for apps, websites, marketing, presentations, and creative projects.";
    // const image = "assets/img/illusbook.png";
    
    //console.log("Router URL:", this.router);
    // const protocol =
    //   this.request.headers["x-forwarded-proto"] || this.request.protocol;
    // const host = this.request.headers.host;
    // const originalUrl = this.request.originalUrl;
    // const fullURL = `${protocol}://${host}${originalUrl}`;
    const fullURL = `https://illusbook.com/collection`;
    
    //console.log("Window URL:", fullURL);
    this.title.setTitle(`${title}`);
    this.setMetaTag("name", "title", title);
    this.setMetaTag("name", "description", desc);
    this.setMetaTag("name", "robots", "index, follow, max-image-preview:large");
    this.setMetaTag("property", "og:title", title);
    this.setMetaTag("property", "og:description", desc);
    this.setMetaTag("property", "og:url", fullURL);
    this.setMetaTag("property", "og:type", "website");
    this.setMetaTag("property", "og:site_name", "Illusbook");
    this.setMetaTag("name", "twitter:card", "summary_large_image");
    this.setMetaTag("name", "twitter:title", title);
    this.setMetaTag("name", "twitter:description", desc);


    const ORGANIZATION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://illusbook.com/#organization",
      "name": "Illusbook",
      "url": "https://illusbook.com",
      "logo": "https://illusbook.com/assets/favicon/android-icon-192x192.png",
      "description": "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@illusbook.com",
        "availableLanguage": ["English"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/illusbook/",
        "https://www.instagram.com/illusbook/",
        "https://x.com/illusbook",
        "https://www.pinterest.com/illusbook/"
      ]
    };

    // 🔥 NEW: Build image gallery for rich results
    this.afs.collection("filesMaster", ref =>
      ref.where("isAIIllustration", "==", true)
        .where("status", "==", "Published")
        .orderBy("createdOn", "desc")
        .limit(5)
    ).valueChanges().pipe(take(1)).subscribe((images: any[]) => {

      const galleryImages = images.map(item => {
        const img = buildSEOImageUrl(item.previewUrl || item.jpeg || item.png);
        return {
          "@type": "ImageObject",
          contentUrl: img,
          thumbnailUrl: img,
          name: item.title || "AI Images",
          "license": "https://illusbook.com/license",
          "acquireLicensePage": "https://illusbook.com/pricing",
          "creditText": "Designed by Illusbook",
          creator: {
            "@type": "Organization",
            name: "Illusbook",
          },
          "copyrightNotice": "Illusbook",
        };
      });

          // Use real image for social preview
    const heroImage = galleryImages[0]?.contentUrl || "https://illusbook.com/assets/img/illusbook.png";

    this.setMetaTag("property", "og:image", heroImage);
    this.setMetaTag("property", "og:image:width", "1200");
    this.setMetaTag("property", "og:image:height", "1200");
    this.setMetaTag("property", "og:image:type", "image/jpeg");
    this.setMetaTag("name", "twitter:image", heroImage);

    // Collection Schema
    const COLLECTION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: desc,
      url: fullURL,
      primaryImageOfPage: galleryImages[0] || {
      "@type": "ImageObject",
      contentUrl: heroImage,
      thumbnailUrl: heroImage,
      name: title
    },
      mainEntity: {
        "@type": "ImageGallery",
        associatedMedia: galleryImages
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://illusbook.com" },
          { "@type": "ListItem", position: 2, name: "AI Images", item: fullURL }
        ]
      },
      publisher: { "@id": "https://illusbook.com/#organization" },
      isPartOf: { "@id": "https://illusbook.com/#website" },
      inLanguage: "en"
    };

      this.seo.setJSONLD([COLLECTION_SCHEMA,
        ORGANIZATION_SCHEMA
      ]);
    });

  }

  setMetaTag(
    attrName: "name" | "property",
    attrValue: string,
    content: string
  ) {
    const selector = `${attrName}="${attrValue}"`;
    //console.log(selector);
    this.meta.removeTag(selector);
    this.meta.addTag({ [attrName]: attrValue, content });
    // this.meta.updateTag({ [attrName]: attrValue, content });
  }
}
